import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateRangePicker,
    Icon,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Textarea,
} from "@elit-hair/elit-ui";
import { captureException } from "@sentry/react";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { TbAlertTriangle, TbArrowLeft, TbCheck, TbCopy, TbEye, TbEyeOff } from "react-icons/tb";
import { toast } from "sonner";

import { Link as AppLink } from "@/components/link";
import { DIMENSION_DEFINITION_LIMIT } from "@/constants/dimensions";
import { useDimensionDefinitions } from "@/queries/dimension-definition.queries";
import { RelatedObject } from "@/types/dimension-definition.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import {
    BULK_CALL_ACTIONS,
    CONCURRENCY_OPTIONS,
    DEFAULT_CONCURRENCY,
    MAX_CALL_IDS,
} from "./constants/bulk-call-actions.constants";
import {
    ACTION_STATUS,
    ActionResultByKey,
    BulkCallAction,
    CallDimensionsByCallId,
    CallDimensionsResult,
} from "./types/bulk-call-actions.types";
import {
    buildDimensionsCsv,
    fetchCallDimensions,
    getActionErrorMessage,
    getActionStatusKey,
    isCreatedWithinRange,
    parseCallIds,
    runWithConcurrency,
} from "./utils/bulk-call-actions.utils";

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_BULK_CALL_ACTIONS)({
    component: BulkCallActionsPage,
});

function ActionIcon({ action, status }: { action: BulkCallAction; status?: ACTION_STATUS }) {
    if (status === ACTION_STATUS.LOADING) return <Spinner size="sm" />;
    if (status === ACTION_STATUS.SUCCESS) return <Icon icon={TbCheck} />;
    if (status === ACTION_STATUS.ERROR) return <Icon icon={TbAlertTriangle} />;

    return <Icon icon={action.icon} />;
}

function DimensionsCell({
    result,
    definitionNameById,
    hiddenDefinitionIds,
    createdRange,
}: {
    result?: CallDimensionsResult;
    definitionNameById: Map<string, string>;
    hiddenDefinitionIds: string[];
    createdRange: DateRange | undefined;
}) {
    if (!result || result.status === ACTION_STATUS.LOADING) return <Spinner size="sm" />;
    if (result.status === ACTION_STATUS.ERROR) return <span className="text-sm text-destructive">{result.error}</span>;
    if (result.dimensions.length === 0) return <span className="text-sm text-muted-foreground">No dimensions</span>;

    const visibleDimensions = result.dimensions.filter(
        (dimension) =>
            !hiddenDefinitionIds.includes(dimension.definitionId) &&
            isCreatedWithinRange(dimension.createdAt, createdRange)
    );

    if (visibleDimensions.length === 0)
        return <span className="text-sm text-muted-foreground">No matching dimensions</span>;

    return (
        <div className="flex flex-col gap-1">
            {visibleDimensions.map((dimension) => (
                <div key={dimension.id} className="line-clamp-2 text-xs" title={dimension.value}>
                    <span className="font-medium">
                        {definitionNameById.get(dimension.definitionId) ?? dimension.definitionId}
                    </span>
                    : <span className="text-muted-foreground">{dimension.value}</span>
                </div>
            ))}
        </div>
    );
}

function getActionButtonClassName(status?: ACTION_STATUS): string | undefined {
    if (status === ACTION_STATUS.SUCCESS) return "text-green-600 hover:bg-green-100 hover:text-green-700";
    if (status === ACTION_STATUS.ERROR) return "text-destructive hover:bg-destructive/10 hover:text-destructive";
    if (status === ACTION_STATUS.LOADING) return "opacity-70";

    return undefined;
}

function BulkCallActionsPage() {
    const [rawIds, setRawIds] = useState("");
    const [callIds, setCallIds] = useState<string[]>([]);
    const [resultByKey, setResultByKey] = useState<ActionResultByKey>({});
    const [runningActionId, setRunningActionId] = useState<string | null>(null);
    const [dimensionsByCallId, setDimensionsByCallId] = useState<CallDimensionsByCallId>({});
    const [isLoadingDimensions, setIsLoadingDimensions] = useState(false);
    const [hiddenDefinitionIds, setHiddenDefinitionIds] = useState<string[]>([]);
    const [concurrency, setConcurrency] = useState(DEFAULT_CONCURRENCY);
    const [createdRange, setCreatedRange] = useState<DateRange | undefined>();

    const definitionsQuery = useDimensionDefinitions({
        relatedObject: RelatedObject.TRANSCRIPT,
        page: 1,
        limit: DIMENSION_DEFINITION_LIMIT,
    });

    const definitionNameById = useMemo(() => {
        const names = new Map<string, string>();
        definitionsQuery.data?.data.forEach((definition) => names.set(definition.id, definition.name));

        return names;
    }, [definitionsQuery.data?.data]);

    const parsedIds = useMemo(() => parseCallIds(rawIds), [rawIds]);
    const exceedsMax = parsedIds.length > MAX_CALL_IDS;

    /** Only the definitions returned for the loaded calls can be toggled. */
    const loadedDefinitions = useMemo(() => {
        const definitionIds = new Set<string>();
        Object.values(dimensionsByCallId).forEach((result) =>
            result.dimensions.forEach((dimension) => definitionIds.add(dimension.definitionId))
        );

        return [...definitionIds]
            .map((id) => ({ id, name: definitionNameById.get(id) ?? id }))
            .sort((first, second) => first.name.localeCompare(second.name));
    }, [dimensionsByCallId, definitionNameById]);

    const visibleDefinitions = useMemo(
        () => loadedDefinitions.filter((definition) => !hiddenDefinitionIds.includes(definition.id)),
        [loadedDefinitions, hiddenDefinitionIds]
    );

    const handleCopyCsv = useCallback(async () => {
        const csv = buildDimensionsCsv({
            callIds,
            dimensionsByCallId,
            columns: visibleDefinitions,
            createdRange,
        });

        await navigator.clipboard.writeText(csv);
        toast.success(`${callIds.length} rows copied as csv`);
    }, [callIds, dimensionsByCallId, visibleDefinitions, createdRange]);

    const toggleDefinition = useCallback((definitionId: string) => {
        setHiddenDefinitionIds((prev) =>
            prev.includes(definitionId) ? prev.filter((id) => id !== definitionId) : [...prev, definitionId]
        );
    }, []);

    const summaries = useMemo(
        () =>
            BULK_CALL_ACTIONS.map((action) => {
                let success = 0;
                let error = 0;
                let lastErrorMessage: string | undefined;

                for (const callId of callIds) {
                    const result = resultByKey[getActionStatusKey(action.id, callId)];
                    if (result?.status === ACTION_STATUS.SUCCESS) success++;
                    if (result?.status === ACTION_STATUS.ERROR) {
                        error++;
                        lastErrorMessage = result.error;
                    }
                }

                return { action, success, error, lastErrorMessage };
            }),
        [callIds, resultByKey]
    );

    const handleLoadIds = useCallback(async () => {
        setCallIds(parsedIds);
        setResultByKey({});
        setDimensionsByCallId(
            Object.fromEntries(parsedIds.map((callId) => [callId, { status: ACTION_STATUS.LOADING, dimensions: [] }]))
        );
        setIsLoadingDimensions(true);

        for (const callId of parsedIds) {
            try {
                const { transcriptId, dimensions } = await fetchCallDimensions(callId);
                setDimensionsByCallId((prev) => ({
                    ...prev,
                    [callId]: { status: ACTION_STATUS.SUCCESS, transcriptId, dimensions },
                }));
            } catch (error) {
                captureException(error);
                setDimensionsByCallId((prev) => ({
                    ...prev,
                    [callId]: { status: ACTION_STATUS.ERROR, dimensions: [], error: getActionErrorMessage(error) },
                }));
            }
        }

        setIsLoadingDimensions(false);
    }, [parsedIds]);

    const handleClear = useCallback(() => {
        setRawIds("");
        setCallIds([]);
        setResultByKey({});
        setDimensionsByCallId({});
        setHiddenDefinitionIds([]);
    }, []);

    const runAction = useCallback(async (action: BulkCallAction, callId: string): Promise<string | undefined> => {
        const key = getActionStatusKey(action.id, callId);
        setResultByKey((prev) => ({ ...prev, [key]: { status: ACTION_STATUS.LOADING } }));

        try {
            await action.run(callId);
            setResultByKey((prev) => ({ ...prev, [key]: { status: ACTION_STATUS.SUCCESS } }));

            return undefined;
        } catch (error) {
            captureException(error);
            const message = getActionErrorMessage(error);
            setResultByKey((prev) => ({ ...prev, [key]: { status: ACTION_STATUS.ERROR, error: message } }));

            return message;
        }
    }, []);

    const handleRunActionForCall = useCallback(
        async (action: BulkCallAction, callId: string) => {
            const errorMessage = await runAction(action, callId);

            if (errorMessage) {
                toast.error(`${action.label} failed for ${callId} — ${errorMessage}`);
            } else {
                toast.success(`${action.label} triggered for ${callId}`);
            }
        },
        [runAction]
    );

    const handleRunActionForAll = useCallback(
        async (action: BulkCallAction) => {
            setRunningActionId(action.id);
            let success = 0;
            let lastErrorMessage: string | undefined;

            await runWithConcurrency(callIds, concurrency, async (callId) => {
                const errorMessage = await runAction(action, callId);
                if (errorMessage) {
                    lastErrorMessage = errorMessage;
                } else {
                    success++;
                }
            });

            setRunningActionId(null);

            if (success === 0 && lastErrorMessage) {
                toast.error(`${action.label} failed for all ${callIds.length} calls — ${lastErrorMessage}`);
            } else {
                toast.success(`${action.label} triggered for ${success} of ${callIds.length} calls`);
            }
        },
        [callIds, concurrency, runAction]
    );

    const isRunning = runningActionId !== null;
    const isBusy = isRunning || isLoadingDimensions;
    const loadedDimensionsCount = Object.values(dimensionsByCallId).filter(
        (result) => result.status !== ACTION_STATUS.LOADING
    ).length;

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
                <AppLink
                    to={APP_ROUTES_TO.DEVELOPER}
                    className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                    <Icon icon={TbArrowLeft} />
                    Testing
                </AppLink>
                <div>
                    <h1 className="text-2xl font-semibold">Bulk call actions</h1>
                    <p className="text-muted-foreground">
                        Paste up to {MAX_CALL_IDS} call ids to see the dimensions of their transcripts and trigger n8n
                        workflows for each one of them.
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base font-medium">Call ids</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                    <Label htmlFor="call-ids">Separated by new lines, commas, semicolons or spaces</Label>
                    <Textarea
                        id="call-ids"
                        value={rawIds}
                        onChange={(event) => setRawIds(event.target.value)}
                        placeholder={"68a1f2c3d4e5f60718293a4b\n68a1f2c3d4e5f60718293a4c"}
                        rows={8}
                        className="font-mono text-sm"
                    />
                    <div className="flex flex-wrap items-center gap-3">
                        <Button onClick={handleLoadIds} disabled={parsedIds.length === 0 || exceedsMax || isBusy}>
                            {isLoadingDimensions && <Spinner size="sm" className="mr-2" />}
                            Load ids
                        </Button>
                        <Button variant="outline" onClick={handleClear} disabled={isBusy}>
                            Clear
                        </Button>
                        <span className={exceedsMax ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
                            {exceedsMax
                                ? `${parsedIds.length} unique ids, only ${MAX_CALL_IDS} are allowed`
                                : `${parsedIds.length} unique ids`}
                        </span>
                        {isLoadingDimensions && (
                            <span className="text-sm text-muted-foreground tabular-nums">
                                {loadedDimensionsCount} of {callIds.length} calls loaded
                            </span>
                        )}
                    </div>
                </CardContent>
            </Card>

            {callIds.length > 0 && (
                <Card>
                    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
                        <CardTitle className="text-base font-medium">{callIds.length} calls loaded</CardTitle>
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                                <Label htmlFor="concurrency" className="text-sm text-muted-foreground">
                                    In parallel
                                </Label>
                                <Select
                                    value={String(concurrency)}
                                    onValueChange={(value) => setConcurrency(Number(value))}
                                    disabled={isBusy}
                                >
                                    <SelectTrigger id="concurrency" className="w-20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CONCURRENCY_OPTIONS.map((option) => (
                                            <SelectItem key={option} value={String(option)}>
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCopyCsv}
                                disabled={visibleDefinitions.length === 0}
                                title="Copies the loaded calls with their visible dimensions"
                            >
                                <Icon icon={TbCopy} />
                                <span className="ml-2">Copy as csv</span>
                            </Button>
                            {summaries.map(({ action, success, error }) => (
                                <div key={action.id} className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleRunActionForAll(action)}
                                        disabled={isBusy}
                                        title={action.description}
                                    >
                                        {runningActionId === action.id ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <Icon icon={action.icon} />
                                        )}
                                        <span className="ml-2">{action.label} for all</span>
                                    </Button>
                                    {success + error > 0 && (
                                        <span className="text-sm text-muted-foreground tabular-nums">
                                            {success} ok / {error} failed
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        {summaries.map(({ action, lastErrorMessage }) =>
                            lastErrorMessage ? (
                                <p
                                    key={action.id}
                                    className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                                >
                                    <span className="font-medium">{action.label}:</span> {lastErrorMessage}
                                </p>
                            ) : null
                        )}

                        {loadedDefinitions.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm text-muted-foreground">Created between:</span>
                                <DateRangePicker
                                    dateRange={createdRange}
                                    onDateRangeChange={setCreatedRange}
                                    showClearButton
                                />
                            </div>
                        )}

                        {loadedDefinitions.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm text-muted-foreground">Show dimensions:</span>
                                {loadedDefinitions.map((definition) => {
                                    const isHidden = hiddenDefinitionIds.includes(definition.id);

                                    return (
                                        <Button
                                            key={definition.id}
                                            variant={isHidden ? "ghost" : "outline"}
                                            size="sm"
                                            onClick={() => toggleDefinition(definition.id)}
                                            className={isHidden ? "text-muted-foreground" : undefined}
                                        >
                                            <Icon icon={isHidden ? TbEyeOff : TbEye} />
                                            <span className="ml-2">{definition.name}</span>
                                        </Button>
                                    );
                                })}
                                {hiddenDefinitionIds.length > 0 && (
                                    <Button variant="ghost" size="sm" onClick={() => setHiddenDefinitionIds([])}>
                                        Show all
                                    </Button>
                                )}
                                {hiddenDefinitionIds.length < loadedDefinitions.length && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                            setHiddenDefinitionIds(loadedDefinitions.map((definition) => definition.id))
                                        }
                                    >
                                        Hide all
                                    </Button>
                                )}
                            </div>
                        )}

                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Call id</TableHead>
                                    <TableHead>Dimensions</TableHead>
                                    <TableHead className="text-right">Options</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {callIds.map((callId, index) => (
                                    <TableRow key={callId}>
                                        <TableCell className="align-top text-muted-foreground tabular-nums">
                                            {index + 1}
                                        </TableCell>
                                        <TableCell className="align-top font-mono text-sm whitespace-nowrap">
                                            {callId}
                                        </TableCell>
                                        <TableCell className="max-w-lg align-top">
                                            <DimensionsCell
                                                result={dimensionsByCallId[callId]}
                                                definitionNameById={definitionNameById}
                                                hiddenDefinitionIds={hiddenDefinitionIds}
                                                createdRange={createdRange}
                                            />
                                        </TableCell>
                                        <TableCell className="align-top">
                                            <div className="flex flex-wrap justify-end gap-2">
                                                {BULK_CALL_ACTIONS.map((action) => {
                                                    const result = resultByKey[getActionStatusKey(action.id, callId)];

                                                    return (
                                                        <Button
                                                            key={action.id}
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleRunActionForCall(action, callId)}
                                                            disabled={
                                                                isBusy || result?.status === ACTION_STATUS.LOADING
                                                            }
                                                            title={result?.error ?? action.description}
                                                            className={getActionButtonClassName(result?.status)}
                                                        >
                                                            <ActionIcon action={action} status={result?.status} />
                                                            <span className="ml-2">{action.label}</span>
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
